import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { PageHeader } from '@/components/app/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { StatusPill } from '@/components/app/StatusPill';
import { formatINR, fmtDate } from '@/lib/format';
import { toast } from 'sonner';
import { Plus, RefreshCw, XCircle, Pencil, Trash2 } from 'lucide-react';
import { useAuth } from '@/lib/auth';

export default function ConsultantLeases() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [props, setProps] = useState([]);
  const [units, setUnits] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [renew, setRenew] = useState(null);
  const [edit, setEdit] = useState(null);
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({ property_id: '', unit_id: '', tenant_id: '', start_date: today, end_date: '', rent_amount: 0, deposit_amount: 0, billing_day: 5, escalation_pct: 10, lock_in_months: 6, notice_period_days: 30 });

  const load = () => api.get('/leases').then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get('/properties').then(r => setProps(r.data));
    api.get('/tenants').then(r => setTenants(r.data));
  }, []);
  useEffect(() => {
    if (form.property_id) api.get('/units', { params: { property_id: form.property_id, status: 'vacant' } }).then(r => setUnits(r.data));
  }, [form.property_id]);

  const submit = async () => {
    try {
      await api.post('/leases', { ...form, rent_amount: Number(form.rent_amount), deposit_amount: Number(form.deposit_amount) });
      toast.success('Lease created'); setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const saveEdit = async () => {
    try {
      const payload = { ...edit, rent_amount: Number(edit.rent_amount), deposit_amount: Number(edit.deposit_amount), billing_day: Number(edit.billing_day), notice_period_days: Number(edit.notice_period_days) };
      await api.put(`/leases/${edit.id}`, payload);
      toast.success('Lease updated'); setEdit(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const doRenew = async () => {
    try {
      await api.post(`/leases/${renew.id}/renew`, null, { params: { new_end_date: renew.new_end_date, new_rent: Number(renew.new_rent) } });
      toast.success('Lease renewed'); setRenew(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const doTerminate = async (id) => {
    if (!window.confirm('Terminate this lease? The unit will be marked vacant.')) return;
    try { await api.post(`/leases/${id}/terminate`); toast.success('Lease terminated'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const doDelete = async (id) => {
    if (!window.confirm('Permanently DELETE this lease? All linked rent demands and payments will also be removed. This cannot be undone.')) return;
    try { await api.delete(`/leases/${id}`); toast.success('Lease deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <PageHeader title="Leases" subtitle="Create, renew and terminate leases" actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-lease-button"><Plus className="h-4 w-4 mr-1" /> New lease</Button></DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Create lease</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><Label>Property</Label><Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v, unit_id: '' })}><SelectTrigger data-testid="lease-property-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{props.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Unit (vacant)</Label><Select value={form.unit_id} onValueChange={(v) => { const u = units.find(x => x.id === v); setForm({ ...form, unit_id: v, rent_amount: u?.rent_amount || 0, deposit_amount: u?.deposit_amount || 0 }); }}><SelectTrigger data-testid="lease-unit-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.unit_number} · {u.configuration}</SelectItem>)}</SelectContent></Select></div>
              <div className="md:col-span-2"><Label>Tenant</Label><Select value={form.tenant_id} onValueChange={(v) => setForm({ ...form, tenant_id: v })}><SelectTrigger data-testid="lease-tenant-select"><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{tenants.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
              <div><Label>Start</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><Label>End</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              <div><Label>Monthly rent (₹)</Label><Input type="number" value={form.rent_amount} onChange={(e) => setForm({ ...form, rent_amount: e.target.value })} /></div>
              <div><Label>Deposit (₹)</Label><Input type="number" value={form.deposit_amount} onChange={(e) => setForm({ ...form, deposit_amount: e.target.value })} /></div>
              <div><Label>Billing day</Label><Input type="number" min="1" max="28" value={form.billing_day} onChange={(e) => setForm({ ...form, billing_day: Number(e.target.value) })} /></div>
              <div><Label>Notice period (days)</Label><Input type="number" value={form.notice_period_days} onChange={(e) => setForm({ ...form, notice_period_days: Number(e.target.value) })} /></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="lease-submit-button">Create</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      } />
      <Card className="p-0 overflow-x-auto">
        <table className="w-full min-w-[640px] text-sm">
          <thead className="bg-muted/60"><tr><th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Agreement</th><th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Tenant</th><th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Property / Unit</th><th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Period</th><th className="text-right px-3 py-2 text-xs uppercase text-muted-foreground">Rent</th><th className="text-left px-3 py-2 text-xs uppercase text-muted-foreground">Status</th><th className="text-right px-3 py-2 text-xs uppercase text-muted-foreground">Actions</th></tr></thead>
          <tbody>
            {items.map((l) => (
              <tr key={l.id} className="border-t hover:bg-muted/40">
                <td className="px-3 py-2 font-mono">{l.agreement_number}</td>
                <td className="px-3 py-2">{l.tenant_name}</td>
                <td className="px-3 py-2">{l.property_name} <span className="text-muted-foreground">/</span> {l.unit_number}</td>
                <td className="px-3 py-2">{fmtDate(l.start_date)} → {fmtDate(l.end_date)}</td>
                <td className="px-3 py-2 text-right tabular-nums">{formatINR(l.rent_amount)}</td>
                <td className="px-3 py-2"><StatusPill status={l.status} /></td>
                <td className="px-3 py-2 text-right space-x-1 whitespace-nowrap">
                  <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => setEdit({ ...l })} data-testid={`lease-edit-${l.id}`} title="Edit"><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => setRenew({ id: l.id, new_end_date: l.end_date, new_rent: l.rent_amount * 1.1 })} title="Renew"><RefreshCw className="h-3.5 w-3.5" /></Button>
                  <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => doTerminate(l.id)} title="Terminate"><XCircle className="h-3.5 w-3.5" /></Button>
                  {isAdmin && (
                    <Button size="sm" variant="outline" className="h-8 px-2 text-destructive hover:text-destructive" onClick={() => doDelete(l.id)} data-testid={`lease-delete-${l.id}`} title="Delete (Super Admin only)"><Trash2 className="h-3.5 w-3.5" /></Button>
                  )}
                </td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={7} className="text-center text-muted-foreground py-8">No leases.</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!renew} onOpenChange={(v) => !v && setRenew(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Renew lease</DialogTitle></DialogHeader>
          {renew && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label>New end date</Label><Input type="date" value={renew.new_end_date} onChange={(e) => setRenew({ ...renew, new_end_date: e.target.value })} /></div>
              <div><Label>New monthly rent</Label><Input type="number" value={renew.new_rent} onChange={(e) => setRenew({ ...renew, new_rent: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter><Button onClick={doRenew} data-testid="lease-renew-submit">Confirm renewal</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit lease</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Start date</Label><Input type="date" value={edit.start_date || ''} onChange={(e) => setEdit({ ...edit, start_date: e.target.value })} /></div>
              <div><Label>End date</Label><Input type="date" value={edit.end_date || ''} onChange={(e) => setEdit({ ...edit, end_date: e.target.value })} /></div>
              <div><Label>Monthly rent (₹)</Label><Input type="number" value={edit.rent_amount || 0} onChange={(e) => setEdit({ ...edit, rent_amount: e.target.value })} /></div>
              <div><Label>Deposit (₹)</Label><Input type="number" value={edit.deposit_amount || 0} onChange={(e) => setEdit({ ...edit, deposit_amount: e.target.value })} /></div>
              <div><Label>Billing day</Label><Input type="number" min="1" max="28" value={edit.billing_day || 5} onChange={(e) => setEdit({ ...edit, billing_day: e.target.value })} /></div>
              <div><Label>Notice period (days)</Label><Input type="number" value={edit.notice_period_days || 30} onChange={(e) => setEdit({ ...edit, notice_period_days: e.target.value })} /></div>
              <div className="sm:col-span-2">
                <Label>Status</Label>
                <Select value={edit.status || 'active'} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="terminated">Terminated</SelectItem>
                    <SelectItem value="renewed">Renewed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={saveEdit} data-testid="lease-edit-save">Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
